import { useState } from 'react';

export default function HomePage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6jbhj3cs59vsrrrjvgg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
        setTimeout(() => setSubmitStatus('idle'), 3000);
      } else {
        setSubmitStatus('error');
        setTimeout(() => setSubmitStatus('idle'), 3000);
      }
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus('idle'), 3000);
    }
  };

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-purple-950 to-black text-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 backdrop-blur-sm">
        <div className="container mx-auto px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://public.readdy.ai/ai/img_res/b55c817d-df34-4b06-b4d0-58cefbe18ff2.png" 
              alt="PRIVAL DOO NIS Logo" 
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-2xl font-medium tracking-wider text-white" style={{ textShadow: '0 0 20px rgba(255,255,255,0.3)' }}>
              PRIVAL DOO NIS
            </h1>
          </div>
          <nav className="flex gap-8">
            <button onClick={() => scrollToSection('home')} className="text-base text-white/90 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">Home</button>
            <button onClick={() => scrollToSection('about')} className="text-base text-white/90 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">About</button>
            <button onClick={() => scrollToSection('products')} className="text-base text-white/90 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">Products</button>
            <button onClick={() => scrollToSection('contact')} className="text-base text-white/90 hover:text-cyan-400 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/50 via-purple-900/30 to-black/50"></div>
        <div className="container mx-auto px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="space-y-8">
              <div className="inline-flex items-center gap-3 px-5 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
                <i className="ri-apple-fill text-xl text-cyan-400"></i>
                <span className="text-sm text-white/90 tracking-wide">iOS Development Experts</span>
              </div>
              <h2 className="text-7xl font-bold leading-tight" style={{ textShadow: '0 4px 20px rgba(0,0,0,0.5)' }}>
                Modern Applications<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500">for iOS Platform</span><br />
                <span className="text-5xl">VPN &amp; Cleaner Solutions</span>
              </h2>
              <p className="text-xl text-white/80 leading-relaxed max-w-xl">
                Enhancing user experience and improving the security of mobile devices through innovative iOS applications.
              </p>
            </div>
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-cyan-500 to-purple-600 p-1">
                <div className="bg-slate-900 rounded-3xl p-8">
                  <img 
                    src="https://public.readdy.ai/ai/img_res/a9a7b1686809a644ccb52268c7ced8ff.jpg" 
                    alt="iOS Applications Showcase"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gradient-to-b from-purple-950/50 to-slate-900/50">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto text-center space-y-8">
            <div className="inline-block">
              <span className="text-xs uppercase tracking-widest text-cyan-400 font-semibold">WHO WE ARE</span>
            </div>
            <h3 className="text-5xl font-bold text-white">About PRIVAL DOO NIS</h3>
            <div className="space-y-6">
              <p className="text-xl text-white/80 leading-relaxed">
                PRIVAL DOO NIS is a team of experts developing modern applications for the iOS platform. We combine technical excellence with innovative design to create applications that users love.
              </p>
              <p className="text-xl text-white/80 leading-relaxed">
                Our products, such as VPN and Cleaner, are designed to enhance user experience and improve the security of mobile devices. We focus on performance, reliability, and user privacy in everything we build.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gradient-to-b from-slate-900 to-purple-950">
        <div className="container mx-auto px-8">
          <div className="text-center mb-20">
            <h3 className="text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-500 mb-4">
              Our iOS Solutions
            </h3>
            <p className="text-xl text-white/70">Powerful applications designed for modern iOS users</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {/* VPN Product Card */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-blue-900 to-blue-700 p-8 hover:scale-105 transition-transform duration-300" style={{ boxShadow: '0 8px 32px rgba(0,217,255,0.2)' }}>
              <div className="absolute inset-0 opacity-5">
                <i className="ri-lock-fill text-[200px] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2"></i>
              </div>
              <div className="relative z-10 space-y-6">
                <div className="w-28 h-28 mx-auto flex items-center justify-center">
                  <i className="ri-shield-check-fill text-8xl text-cyan-400" style={{ textShadow: '0 0 30px rgba(0,217,255,0.6)' }}></i>
                </div>
                <h4 className="text-3xl font-bold text-white text-center">VPN Solution</h4>
                <ul className="space-y-4 pt-4">
                  <li className="flex items-center gap-3 text-lg text-cyan-200">
                    <i className="ri-check-line text-2xl text-cyan-400"></i>
                    <span>Secure Connection</span>
                  </li>
                  <li className="flex items-center gap-3 text-lg text-cyan-200">
                    <i className="ri-check-line text-2xl text-cyan-400"></i>
                    <span>Global Servers</span>
                  </li>
                  <li className="flex items-center gap-3 text-lg text-cyan-200">
                    <i className="ri-check-line text-2xl text-cyan-400"></i>
                    <span>Privacy Protection</span>
                  </li>
                  <li className="flex items-center gap-3 text-lg text-cyan-200">
                    <i className="ri-check-line text-2xl text-cyan-400"></i>
                    <span>Fast Performance</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cleaner Product Card */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-purple-600 to-pink-600 p-8 hover:scale-105 transition-transform duration-300" style={{ boxShadow: '0 8px 32px rgba(139,92,246,0.3)' }}>
              <div className="absolute top-4 right-4 w-32 h-32 rounded-full bg-white/10 blur-2xl"></div>
              <div className="absolute bottom-4 left-4 w-24 h-24 rounded-full bg-white/10 blur-2xl"></div>
              <div className="relative z-10 space-y-6">
                <div className="w-32 h-32 mx-auto flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
                  <i className="ri-brush-fill text-7xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white text-center">Cleaner</h4>
                <p className="text-lg italic text-white/90 text-center">Optimize Your Device</p>
                <ul className="space-y-4 pt-4">
                  <li className="flex items-center gap-3 text-lg text-white/90">
                    <i className="ri-check-line text-2xl text-white"></i>
                    <span>Storage Optimization</span>
                  </li>
                  <li className="flex items-center gap-3 text-lg text-white/90">
                    <i className="ri-check-line text-2xl text-white"></i>
                    <span>Cache Cleaning</span>
                  </li>
                  <li className="flex items-center gap-3 text-lg text-white/90">
                    <i className="ri-check-line text-2xl text-white"></i>
                    <span>Speed Boost</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Combined Features Card */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-purple-700 to-violet-900 hover:scale-105 transition-transform duration-300" style={{ boxShadow: '0 8px 32px rgba(139,92,246,0.2)' }}>
              <div className="h-64 relative overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=abstract%20modern%20iOS%20mobile%20interface%20mockup%20with%20clean%20minimalist%20design%20showing%20security%20features%20and%20performance%20metrics%20holographic%20UI%20elements%20floating%20in%20purple%20and%20violet%20gradient%20background%20futuristic%20technology%20visualization&width=400&height=300&seq=feat001&orientation=landscape" 
                  alt="iOS Interface"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="p-8 bg-gradient-to-t from-black/80 to-transparent space-y-4">
                <h4 className="text-3xl font-bold text-white">Enhanced Security</h4>
                <h5 className="text-2xl font-medium text-white/90">Performance Optimization</h5>
                <p className="text-base text-white/80 leading-relaxed">
                  Our applications combine advanced security features with performance optimization to deliver the best iOS experience.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-gradient-to-b from-slate-900 to-black">
        <div className="container mx-auto px-8">
          <div className="bg-slate-900/50 backdrop-blur-sm rounded-[40px] p-16 border border-white/10">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-16">
              {/* Contact Form */}
              <div className="lg:col-span-2">
                <h3 className="text-5xl font-light text-white mb-4">Get In Touch</h3>
                <p className="text-lg text-white/70 mb-12">Have questions about our iOS applications? We'd love to hear from you.</p>
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-cyan-400/50 text-white text-base py-4 px-0 focus:outline-none focus:border-cyan-400 transition-colors placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email Address"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="w-full bg-transparent border-b border-cyan-400/50 text-white text-base py-4 px-0 focus:outline-none focus:border-cyan-400 transition-colors placeholder:text-white/40"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={(e) => {
                        if (e.target.value.length <= 500) {
                          setFormData({ ...formData, message: e.target.value });
                        }
                      }}
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full bg-transparent border-b border-cyan-400/50 text-white text-base py-4 px-0 focus:outline-none focus:border-cyan-400 transition-colors placeholder:text-white/40 resize-none"
                    />
                    <p className="text-sm text-white/50 mt-2">{formData.message.length}/500 characters</p>
                  </div>
                  <button
                    type="submit"
                    disabled={submitStatus === 'submitting'}
                    className="inline-flex items-center gap-3 px-12 py-4 bg-white text-black rounded-full text-base font-medium hover:bg-cyan-400 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                  >
                    <span>{submitStatus === 'submitting' ? 'Sending...' : 'Send Message'}</span>
                    <i className="ri-arrow-right-line text-xl"></i>
                  </button>
                  {submitStatus === 'success' && (
                    <p className="text-green-400 text-base">Message sent successfully!</p>
                  )}
                  {submitStatus === 'error' && (
                    <p className="text-red-400 text-base">Failed to send message. Please try again.</p>
                  )}
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-12">
                <div>
                  <p className="text-sm uppercase tracking-widest text-cyan-400 font-semibold mb-6">CONTACT</p>
                  <div className="space-y-4">
                    <div className="flex items-center gap-3">
                      <i className="ri-phone-fill text-xl text-cyan-400"></i>
                      <a href="tel:+15074287029" className="text-lg text-white hover:text-cyan-400 transition-colors cursor-pointer">
                        +1 5074287029
                      </a>
                    </div>
                    <div className="flex items-center gap-3">
                      <i className="ri-mail-fill text-xl text-cyan-400"></i>
                      <a href="mailto:help@privaldoonis.com" className="text-lg text-white hover:text-cyan-400 transition-colors cursor-pointer">
                        help@privaldoonis.com
                      </a>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="text-sm uppercase tracking-widest text-cyan-400 font-semibold mb-6">LOCATION</p>
                  <div className="space-y-2 text-base text-white/70">
                    <p>VAZDUHOPLOVACA 24</p>
                    <p>Nis, 704220</p>
                    <p>Serbia</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="mt-16 pt-10 border-t border-white/10">
              <div className="text-center">
                <p className="text-sm text-white/60">
                  © 2025 PRIVAL DOO NIS. All rights reserved.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}